import { NgClass } from '@angular/common';
import { Component, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronRight, faCircleArrowLeft, faHouse } from '@fortawesome/free-solid-svg-icons';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { NavbarStateService } from '../../services/navbar-state/navbar-state.service';

type IBreadCrumb = {
  label: string;
  url: string;
  sibling?: IBreadCrumb;
};

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [RouterLink, FaIconComponent, NgClass],
})
export class BreadcrumbComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private readonly navbarState = inject(NavbarStateService);

  breadcrumbs: WritableSignal<IBreadCrumb[]> = signal([]);
  faHouse = faHouse;
  faChevronRight = faChevronRight;
  faArrowLeft = faCircleArrowLeft;

  get isNavCollapse() {
    return this.navbarState.navCollapse();
  }
  constructor() {
    this.breadcrumbs.set(this.buildBreadCrumb(this.activatedRoute.root));
  }

  ngOnInit() {
    this.router.events
      .pipe(
        tap((event) => {
          if (event instanceof NavigationStart) {
            sessionStorage.setItem('previousUrl', this.router.url);
          }
        }),
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged(),
      )
      .subscribe((event: NavigationEnd) => {
        if (window.screen.availWidth < 768) {
          const crumbs = this.buildBreadCrumb(this.activatedRoute.root);
          this.breadcrumbs.set([{ label: '...', url: '' }, ...crumbs.slice(crumbs.length - 1)]);
        } else {
          this.breadcrumbs.set(this.buildBreadCrumb(this.activatedRoute.root));
        }
      });
  }

  buildBreadCrumb(route: ActivatedRoute, breadcrumbs: IBreadCrumb[] = []): IBreadCrumb[] {
    const bc = route.snapshot.data && route.snapshot.data['breadcrumb'] ? route.snapshot.data['breadcrumb'] : null;
    if (bc !== null) {
      if (bc.url !== null && bc.url.split(':').length > 1) {
        const param = route.snapshot.params[bc.url.split(':')[1]];
        if (bc.label === ':' + bc.url.split(':')[1]) {
          bc.label = param;
        }
        bc.url = bc.url.replace(`:${bc.url.split(':')[1]}`, param);
      }
      if (bc.sibling) {
        const prevUrl = sessionStorage.getItem('previousUrl');
        const sibling = bc.sibling;
        if (sibling.url !== null && sibling.url !== undefined) {
          if (sibling.url.split(':').length > 1) {
            const param = route.snapshot.params[sibling.url.split(':')[1]];
            sibling.url = sibling.url.replace(`:${sibling.url.split(':')[1]}`, param);
            if (sibling.label === ':' + bc.url.split(':')[1]) {
              sibling.label = param;
            }
          }
        }
        if (prevUrl !== null && !prevUrl.includes(sibling.defaultUrl)) {
          sibling.url = sibling.defaultUrl;
        }
        breadcrumbs = bc !== null ? [...breadcrumbs, sibling, bc] : breadcrumbs;
      } else {
        breadcrumbs = bc !== null ? [...breadcrumbs, bc] : breadcrumbs;
      }
    }
    return route.firstChild ? this.buildBreadCrumb(route.firstChild, breadcrumbs) : breadcrumbs;
  }

  back(url: string | null = '') {
    if (url === null) {
      window.history.back();
    } else {
      this.router.navigate([url]);
    }
  }
}
